/**
 * Geometry Calculator Dialog
 *
 * Material-UI dialog for configuring geometry property calculations.
 * Provides property selection checkboxes and unit configuration.
 *
 * **Validates: Requirements 52.1, 52.6**
 */

import React, { useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  CircularProgress,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Switch,
  Divider,
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState, AppDispatch } from '../../store';
import type { GeometricProperty, LengthUnit, AreaUnit } from '../../services/GeometryProcessor';
import {
  setCalculatorSourceLayer,
  toggleCalculatorProperty,
  setCalculatorLengthUnit,
  setCalculatorAreaUnit,
  setCalculatorAutoUpdate,
  closeTool,
} from '../../store/slices/geometryProcessingSlice';

interface GeometryCalculatorDialogProps {
  open: boolean;
  onExecute: () => void;
}

const GEOMETRIC_PROPERTIES: {
  value: GeometricProperty;
  label: string;
  description: string;
  types: string[];
}[] = [
  {
    value: 'area',
    label: 'Area',
    description: 'Surface area for polygons',
    types: ['Polygon', 'MultiPolygon'],
  },
  {
    value: 'perimeter',
    label: 'Perimeter',
    description: 'Boundary length for polygons',
    types: ['Polygon', 'MultiPolygon'],
  },
  {
    value: 'length',
    label: 'Length',
    description: 'Line length',
    types: ['LineString', 'MultiLineString'],
  },
  {
    value: 'x-coordinate',
    label: 'X Coordinate',
    description: 'Longitude (centroid for polygons/lines)',
    types: ['Point', 'LineString', 'Polygon', 'MultiPolygon', 'MultiLineString'],
  },
  {
    value: 'y-coordinate',
    label: 'Y Coordinate',
    description: 'Latitude (centroid for polygons/lines)',
    types: ['Point', 'LineString', 'Polygon', 'MultiPolygon', 'MultiLineString'],
  },
];

const LENGTH_UNITS: { value: LengthUnit; label: string }[] = [
  { value: 'meters', label: 'Meters (m)' },
  { value: 'kilometers', label: 'Kilometers (km)' },
  { value: 'feet', label: 'Feet (ft)' },
  { value: 'miles', label: 'Miles (mi)' },
];

const AREA_UNITS: { value: AreaUnit; label: string }[] = [
  { value: 'square-meters', label: 'Square Meters (m²)' },
  { value: 'square-kilometers', label: 'Square Kilometers (km²)' },
  { value: 'hectares', label: 'Hectares (ha)' },
  { value: 'acres', label: 'Acres (ac)' },
];

export const GeometryCalculatorDialog: React.FC<GeometryCalculatorDialogProps> = ({
  open,
  onExecute,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { calculatorTool, isProcessing, error, message } = useSelector(
    (state: RootState) => state.geometryProcessing
  );
  const layers = useSelector((state: RootState) => state.layers.layers);

  const selectedLayer = useMemo(
    () => layers.find((l) => l.id === calculatorTool.sourceLayerId),
    [layers, calculatorTool.sourceLayerId]
  );

  // Filter available properties based on layer geometry type
  const availableProperties = useMemo(() => {
    if (!selectedLayer?.geometryType) return GEOMETRIC_PROPERTIES;
    return GEOMETRIC_PROPERTIES.filter((p) => p.types.includes(selectedLayer.geometryType!));
  }, [selectedLayer]);

  const handleClose = useCallback(() => {
    dispatch(closeTool());
  }, [dispatch]);

  const handleSourceLayerChange = useCallback(
    (e: any) => {
      dispatch(setCalculatorSourceLayer(e.target.value));
    },
    [dispatch]
  );

  const handlePropertyToggle = useCallback(
    (property: GeometricProperty) => {
      dispatch(toggleCalculatorProperty(property));
    },
    [dispatch]
  );

  const handleLengthUnitChange = useCallback(
    (e: any) => {
      dispatch(setCalculatorLengthUnit(e.target.value as LengthUnit));
    },
    [dispatch]
  );

  const handleAreaUnitChange = useCallback(
    (e: any) => {
      dispatch(setCalculatorAreaUnit(e.target.value as AreaUnit));
    },
    [dispatch]
  );

  const handleAutoUpdateChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(setCalculatorAutoUpdate(e.target.checked));
    },
    [dispatch]
  );

  const vectorLayers = useMemo(() => layers.filter((l) => l.type === 'vector'), [layers]);

  // Check if area/perimeter properties are selected (need area unit)
  const needsAreaUnit = calculatorTool.selectedProperties.some((p) => p === 'area');
  const needsLengthUnit = calculatorTool.selectedProperties.some(
    (p) => p === 'perimeter' || p === 'length'
  );

  const canExecute =
    calculatorTool.sourceLayerId && calculatorTool.selectedProperties.length > 0 && !isProcessing;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Geometry Calculator</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Calculate geometric properties and add them as new attribute fields.
          </Typography>

          <FormControl fullWidth>
            <InputLabel id="calc-source-layer-label">Source Layer</InputLabel>
            <Select
              labelId="calc-source-layer-label"
              value={calculatorTool.sourceLayerId || ''}
              onChange={handleSourceLayerChange}
              label="Source Layer"
            >
              {vectorLayers.map((layer) => (
                <MenuItem key={layer.id} value={layer.id}>
                  {layer.name} ({layer.geometryType || 'Mixed'})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {calculatorTool.sourceLayerId && (
            <>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Properties to Calculate
                </Typography>
                <FormGroup>
                  {availableProperties.map((prop) => (
                    <FormControlLabel
                      key={prop.value}
                      control={
                        <Checkbox
                          checked={calculatorTool.selectedProperties.includes(prop.value)}
                          onChange={() => handlePropertyToggle(prop.value)}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2">{prop.label}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {prop.description}
                          </Typography>
                        </Box>
                      }
                    />
                  ))}
                </FormGroup>
              </Box>

              {needsAreaUnit && (
                <>
                  <Divider />
                  <FormControl fullWidth>
                    <InputLabel id="calc-area-unit-label">Area Unit</InputLabel>
                    <Select
                      labelId="calc-area-unit-label"
                      value={calculatorTool.areaUnit}
                      onChange={handleAreaUnitChange}
                      label="Area Unit"
                    >
                      {AREA_UNITS.map((unit) => (
                        <MenuItem key={unit.value} value={unit.value}>
                          {unit.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </>
              )}

              {needsLengthUnit && (
                <>
                  {!needsAreaUnit && <Divider />}
                  <FormControl fullWidth>
                    <InputLabel id="calc-length-unit-label">Length/Perimeter Unit</InputLabel>
                    <Select
                      labelId="calc-length-unit-label"
                      value={calculatorTool.lengthUnit}
                      onChange={handleLengthUnitChange}
                      label="Length/Perimeter Unit"
                    >
                      {LENGTH_UNITS.map((unit) => (
                        <MenuItem key={unit.value} value={unit.value}>
                          {unit.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </>
              )}

              <Divider />

              <FormControlLabel
                control={
                  <Switch checked={calculatorTool.autoUpdate} onChange={handleAutoUpdateChange} />
                }
                label="Auto-update when geometry changes"
              />
            </>
          )}

          {error && <Alert severity="error">{error}</Alert>}
          {message && !error && <Alert severity="success">{message}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isProcessing}>
          Cancel
        </Button>
        <Button
          onClick={onExecute}
          variant="contained"
          disabled={!canExecute}
          startIcon={isProcessing ? <CircularProgress size={16} /> : undefined}
        >
          {isProcessing ? 'Processing...' : 'Calculate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GeometryCalculatorDialog;
